/**
 * LabTwinNetworkPage — iter1113.c
 *
 * Interactive visualization showing which lab marker unlocks which
 * digital twin. Left column lists labs (grouped by panel), right column
 * lists twins. SVG edges connect them; hovering/clicking a lab
 * highlights its connected twins and vice versa.
 *
 * Backend: `/api/lab-twin-map` (static authoritative map).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { ArrowLeft, Network, Info } from "lucide-react";
import { Loader2 } from "lucide-react";
import { MobileTopBar } from "@/components/MobileTopBar";

const ROLE_COLOR = {
  primary: "#10b981", // emerald — main input
  support: "#60a5fa", // sky — co-factor
  display: "#a78bfa", // violet — educational only
};
const ROLE_LABEL = {
  primary: "Основен вход",
  support: "Допълващ",
  display: "Образователен",
};

export default function LabTwinNetworkPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null); // {kind:'lab'|'twin', key:'...'}
  const [click, setClick] = useState(null); // sticky selection
  const [panelFilter, setPanelFilter] = useState("Всички");

  const svgRef = useRef(null);
  const labRefs = useRef({});
  const twinRefs = useRef({});
  const [edgeCoords, setEdgeCoords] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/lab-twin-map`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [API]);

  const panels = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.labs.map((l) => l.panel)));
  }, [data]);

  const filteredLabs = useMemo(() => {
    if (!data) return [];
    if (panelFilter === "Всички") return data.labs;
    return data.labs.filter((l) => l.panel === panelFilter);
  }, [data, panelFilter]);

  const groupedLabs = useMemo(() => {
    const g = {};
    filteredLabs.forEach((l) => {
      if (!g[l.panel]) g[l.panel] = [];
      g[l.panel].push(l);
    });
    return g;
  }, [filteredLabs]);

  // Which key is "active" — hover takes precedence, then click.
  const active = hover || click;

  // Compute active edges.
  const activeEdges = useMemo(() => {
    if (!active || !data) return new Set();
    const set = new Set();
    data.edges.forEach((e, i) => {
      if (
        (active.kind === "lab" && e.lab === active.key) ||
        (active.kind === "twin" && e.twin === active.key)
      ) {
        set.add(i);
      }
    });
    return set;
  }, [active, data]);

  // Recompute SVG edge positions when layout, data, or filter changes.
  const recomputeEdges = () => {
    if (!data || !svgRef.current) return;
    const svgBox = svgRef.current.getBoundingClientRect();
    const coords = [];
    data.edges.forEach((e, i) => {
      const labEl = labRefs.current[e.lab];
      const twinEl = twinRefs.current[e.twin];
      if (!labEl || !twinEl) return;
      const l = labEl.getBoundingClientRect();
      const t = twinEl.getBoundingClientRect();
      coords.push({
        i,
        x1: l.right - svgBox.left,
        y1: l.top + l.height / 2 - svgBox.top,
        x2: t.left - svgBox.left,
        y2: t.top + t.height / 2 - svgBox.top,
        role: e.role,
        lab: e.lab,
        twin: e.twin,
      });
    });
    setEdgeCoords(coords);
  };

  useEffect(() => {
    if (!data) return;
    const run = () => requestAnimationFrame(recomputeEdges);
    run();
    window.addEventListener("resize", run);
    window.addEventListener("scroll", run, true);
    return () => {
      window.removeEventListener("resize", run);
      window.removeEventListener("scroll", run, true);
    };
    // eslint-disable-next-line
  }, [data, panelFilter]);

  const handleLabClick = (key) =>
    setClick((cur) => (cur?.kind === "lab" && cur.key === key ? null : { kind: "lab", key }));
  const handleTwinClick = (key) =>
    setClick((cur) => (cur?.kind === "twin" && cur.key === key ? null : { kind: "twin", key }));

  // Dim non-active chips when there's a selection.
  const isDim = (kind, key) => {
    if (!active) return false;
    if (active.kind === kind && active.key === key) return false;
    // if a lab is active, highlight only its connected twins (and vice versa)
    const connected = data.edges.some((e) =>
      active.kind === "lab"
        ? e.lab === active.key && (kind === "twin" && e.twin === key)
        : e.twin === active.key && (kind === "lab" && e.lab === key)
    );
    if (connected) return false;
    // Same column peers → dim
    return true;
  };

  const bg = isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900";
  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const chipBase =
    "px-2 py-1.5 rounded-lg text-[11px] leading-tight cursor-pointer transition-all duration-200 border select-none";

  return (
    <div className={`min-h-screen ${bg}`} data-testid="lab-twin-network-page">
      <MobileTopBar title="Лабораторни показатели → Близнаци" fallbackTo="/dashboard" testid="lab-twin-network-topbar" />

      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        <div className="flex items-center gap-2 mb-3">
          <Network className="w-5 h-5 text-cyan-500" />
          <h1 className="text-xl font-bold">Връзки: лабораторни показатели и близнаци</h1>
        </div>

        <p className={`text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Вижте кой лабораторен маркер отключва кой дигитален близнак. Докоснете
          или посочете с показалеца на мишката маркер или близнак, за да осветите
          връзките му.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
          </div>
        ) : !data ? (
          <p className="opacity-60 text-sm">Няма налични данни.</p>
        ) : (
          <>
            {/* Stats */}
            <div
              className={`p-3 rounded-xl border mb-4 flex flex-wrap items-center gap-3 text-xs ${cardCls}`}
              data-testid="network-stats"
            >
              <span className="font-bold">
                {data.stats.lab_count} маркера → {data.stats.twin_count} близнака
              </span>
              <span className="opacity-70">·</span>
              <span>{data.stats.edge_count} връзки</span>
              <span className="opacity-70">·</span>
              <span className="text-emerald-500 font-semibold">
                {data.stats.primary_edges} основни
              </span>
              {active && (
                <button
                  onClick={() => setClick(null)}
                  className="ml-auto text-[11px] px-2 py-1 rounded-md bg-slate-500/15 hover:bg-slate-500/25"
                  data-testid="clear-selection-btn"
                >
                  Изчистете избора
                </button>
              )}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 mb-4 text-[11px]">
              {Object.keys(ROLE_LABEL).map((r) => (
                <div key={r} className="flex items-center gap-1.5">
                  <span
                    className="inline-block w-5 h-[2px] rounded"
                    style={{ backgroundColor: ROLE_COLOR[r] }}
                  />
                  <span className="opacity-80">{ROLE_LABEL[r]}</span>
                </div>
              ))}
              <div className="flex items-center gap-1 opacity-70 ml-auto">
                <Info className="w-3 h-3" />
                <span>Натиснете за задържане на избора</span>
              </div>
            </div>

            {/* Panel filter chips */}
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="panel-filter">
              {["Всички", ...panels].map((p) => (
                <button
                  key={p}
                  onClick={() => setPanelFilter(p)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                    panelFilter === p
                      ? "bg-cyan-500 text-white border-cyan-500"
                      : isDark
                      ? "bg-slate-800/50 border-slate-700 hover:border-cyan-500/50"
                      : "bg-white border-slate-200 hover:border-cyan-500/50"
                  }`}
                  data-testid={`panel-filter-${p}`}
                >
                  {p}
                </button>
              ))}
            </div>

            {/* Network */}
            <div className="relative" data-testid="network-grid">
              {/* SVG edges layer */}
              <svg
                ref={svgRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ zIndex: 1 }}
              >
                {edgeCoords.map((c) => {
                  const isActive = activeEdges.has(c.i);
                  const dim = active && !isActive;
                  return (
                    <path
                      key={c.i}
                      d={`M ${c.x1} ${c.y1} C ${c.x1 + 60} ${c.y1}, ${c.x2 - 60} ${c.y2}, ${c.x2} ${c.y2}`}
                      stroke={ROLE_COLOR[c.role]}
                      strokeWidth={isActive ? 2.5 : 1}
                      strokeOpacity={dim ? 0.08 : isActive ? 0.95 : 0.35}
                      fill="none"
                    />
                  );
                })}
              </svg>

              {/* Two-column layout */}
              <div className="grid grid-cols-2 gap-4 relative" style={{ zIndex: 2 }}>
                {/* Labs column */}
                <div
                  className={`p-3 rounded-xl border ${cardCls}`}
                  data-testid="labs-column"
                >
                  <h3 className="text-xs font-bold mb-2 opacity-70">
                    Лабораторни маркери
                  </h3>
                  {Object.entries(groupedLabs).map(([panel, labs]) => (
                    <div key={panel} className="mb-3 last:mb-0">
                      <div className="text-[10px] uppercase opacity-50 font-bold mb-1">
                        {panel}
                      </div>
                      <div className="space-y-1.5">
                        {labs.map((l) => {
                          const isActive =
                            active?.kind === "lab" && active.key === l.key;
                          const dim = isDim("lab", l.key);
                          return (
                            <div
                              key={l.key}
                              ref={(el) => (labRefs.current[l.key] = el)}
                              onMouseEnter={() =>
                                setHover({ kind: "lab", key: l.key })
                              }
                              onMouseLeave={() => setHover(null)}
                              onClick={() => handleLabClick(l.key)}
                              className={`${chipBase} ${
                                isActive
                                  ? "border-cyan-500 ring-2 ring-cyan-500/40 shadow-md"
                                  : isDark
                                  ? "border-slate-700 hover:border-cyan-500/50 bg-slate-800/40"
                                  : "border-slate-200 hover:border-cyan-500/50 bg-slate-50"
                              } ${dim ? "opacity-30" : ""}`}
                              data-testid={`lab-chip-${l.key}`}
                            >
                              <div className="font-semibold">{l.label_bg}</div>
                              <div className="opacity-50 text-[10px]">
                                {l.unit}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Twins column */}
                <div
                  className={`p-3 rounded-xl border ${cardCls}`}
                  data-testid="twins-column"
                >
                  <h3 className="text-xs font-bold mb-2 opacity-70">
                    Дигитални близнаци
                  </h3>
                  <div className="space-y-1.5">
                    {data.twins.map((t) => {
                      const isActive =
                        active?.kind === "twin" && active.key === t.key;
                      const dim = isDim("twin", t.key);
                      return (
                        <div
                          key={t.key}
                          ref={(el) => (twinRefs.current[t.key] = el)}
                          onMouseEnter={() =>
                            setHover({ kind: "twin", key: t.key })
                          }
                          onMouseLeave={() => setHover(null)}
                          onClick={() => handleTwinClick(t.key)}
                          className={`${chipBase} flex items-center gap-2 ${
                            isActive
                              ? "border-cyan-500 ring-2 ring-cyan-500/40 shadow-md"
                              : isDark
                              ? "border-slate-700 hover:border-cyan-500/50 bg-slate-800/40"
                              : "border-slate-200 hover:border-cyan-500/50 bg-slate-50"
                          } ${dim ? "opacity-30" : ""}`}
                          data-testid={`twin-chip-${t.key}`}
                        >
                          <span className="text-base" aria-hidden>
                            {t.emoji}
                          </span>
                          <div className="font-semibold">{t.label_bg}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Active selection detail */}
            {active && (
              <div
                className={`mt-4 p-4 rounded-xl border ${cardCls}`}
                data-testid="active-detail"
              >
                {(() => {
                  if (active.kind === "lab") {
                    const lab = data.labs.find((l) => l.key === active.key);
                    const connectedTwins = data.edges
                      .filter((e) => e.lab === active.key)
                      .map((e) => ({
                        ...data.twins.find((t) => t.key === e.twin),
                        role: e.role,
                      }));
                    return (
                      <>
                        <h4 className="font-bold text-sm">
                          {lab?.label_bg} отключва {connectedTwins.length} близнака
                        </h4>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {connectedTwins.map((t) => (
                            <span
                              key={t.key}
                              className={`text-[11px] px-2 py-1 rounded-md font-semibold flex items-center gap-1`}
                              style={{
                                background: `${ROLE_COLOR[t.role]}22`,
                                color: ROLE_COLOR[t.role],
                              }}
                            >
                              {t.emoji} {t.label_bg} · {ROLE_LABEL[t.role]}
                            </span>
                          ))}
                        </div>
                      </>
                    );
                  }
                  const twin = data.twins.find((t) => t.key === active.key);
                  const connectedLabs = data.edges
                    .filter((e) => e.twin === active.key)
                    .map((e) => ({
                      ...data.labs.find((l) => l.key === e.lab),
                      role: e.role,
                    }));
                  return (
                    <>
                      <h4 className="font-bold text-sm">
                        {twin?.emoji} {twin?.label_bg} чете {connectedLabs.length} маркера
                      </h4>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {connectedLabs.map((l) => (
                          <span
                            key={l.key}
                            className={`text-[11px] px-2 py-1 rounded-md font-semibold`}
                            style={{
                              background: `${ROLE_COLOR[l.role]}22`,
                              color: ROLE_COLOR[l.role],
                            }}
                          >
                            {l.label_bg} · {ROLE_LABEL[l.role]}
                          </span>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
